import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUp,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  Shield,
  Zap,
  Users,
  CreditCard,
  AlertCircle,
  Info,
  MapPin,
  Lock,
  Clock,
  FileText,
  TrendingUp,
  Eye,
  Heart,
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/TermsAndServices.css';

gsap.registerPlugin(ScrollTrigger);

const TermsAndServices = () => {
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('eligibility');
  const [expandedSections, setExpandedSections] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const sectionsRef = useRef({});

  useEffect(() => {
    window.scrollTo(0, 0);

    gsap.fromTo(
      headerRef.current,
      { opacity: 0, y: -40 },
      { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }
    );

    Object.values(sectionsRef.current).forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 25 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            delay: 0.08 + index * 0.04,
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    });

    // Handle hash navigation
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        const element = sectionsRef.current[hash];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveSection(hash);
        }
      }, 100);
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);

      // Calculate scroll progress
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (window.scrollY / docHeight) * 100;
      setScrollProgress(scrolled);

      // Update active section
      Object.keys(sectionsRef.current).forEach((key) => {
        const element = sectionsRef.current[key];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top < window.innerHeight / 3) {
            setActiveSection(key);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpand = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const scrollToSection = (sectionId) => {
    const element = sectionsRef.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  const mainSections = [
    {
      id: 'eligibility',
      type: 'summary',
      title: 'Eligibility & Accounts',
      shortLabel: 'Eligibility',
      icon: <Users size={18} />,
      tldr: 'You must be 18+ with accurate information to use Sync and Explore.',
      content:
        'Users must be at least 18 years old and provide accurate account information. Accounts may be suspended for misuse or false data.',
    },
    {
      id: 'services',
      type: 'summary',
      title: 'Our Services',
      shortLabel: 'Services',
      icon: <Zap size={18} />,
      tldr: 'We provide AI travel planning, matching, and booking facilitation tools.',
      content:
        'Sync and Explore provides AI-based travel planning, itinerary generation, matching features, and booking facilitation through third-party providers. The company acts as a technology platform, not a travel agent.',
    },
    {
      id: 'payments',
      type: 'summary',
      title: 'Bookings & Payments',
      shortLabel: 'Payments',
      icon: <CreditCard size={18} />,
      tldr: 'Bookings are subject to third-party terms. We handle facilitation only.',
      content:
        'Bookings are subject to third-party provider terms. Sync and Explore is not responsible for cancellations, delays, or service quality of third parties.',
    },
    {
      id: 'liability',
      type: 'highlight',
      title: 'Limitation of Liability',
      shortLabel: 'Liability',
      icon: <Shield size={18} />,
      tldr: 'We\'re not liable for indirect losses from platform usage or travel decisions.',
      content:
        'The company is not liable for indirect or consequential losses arising from platform usage or travel decisions.',
    },
  ];

  const policyTitleSections = [
    {
      id: 'privacy',
      type: 'expandable',
      title: 'Privacy Policy',
      shortLabel: 'Privacy',
      icon: <Lock size={18} />,
      subsections: [
        {
          subtitle: 'Information Collected',
          content: 'Personal details, travel preferences, usage data, and technical data may be collected. Payment data is handled securely by third-party gateways.',
        },
        {
          subtitle: 'Use of Information',
          content: 'Data is used to provide services, generate AI recommendations, process bookings, improve security, and enhance user experience.',
        },
        {
          subtitle: 'Data Sharing & Security',
          content: 'Information may be shared with service providers and legal authorities when required. We do not sell personal data.',
        },
      ],
    },
    {
      id: 'community',
      type: 'expandable',
      title: 'Community Guidelines',
      shortLabel: 'Community',
      icon: <Users size={18} />,
      subsections: [
        {
          subtitle: 'Prohibited Conduct',
          content: 'Harassment, scams, explicit content, impersonation, and misuse of matching features are strictly prohibited.',
        },
        {
          subtitle: 'User Safety',
          content: 'Sync and Explore does not conduct background checks. Meetings arranged through the platform are at users\' own risk.',
        },
      ],
    },
    {
      id: 'refunds',
      type: 'expandable',
      title: 'Refund & Cancellation Policy',
      shortLabel: 'Refunds',
      icon: <CreditCard size={18} />,
      subsections: [
        {
          subtitle: 'General Policy',
          content: 'Sync and Explore acts as a facilitator. Refunds and cancellations depend on third-party provider policies.',
        },
        {
          subtitle: 'Platform Fees',
          content: 'Convenience or AI planning fees are non-refundable unless explicitly stated.',
        },
        {
          subtitle: 'Failed Payments',
          content: 'Duplicate or failed payments will be refunded after verification within standard banking timelines.',
        },
      ],
    },
    {
      id: 'safety',
      type: 'highlight',
      title: 'Safety Tips & User Advisory',
      shortLabel: 'Safety',
      icon: <AlertCircle size={18} />,
      subsections: [
        {
          subtitle: 'Travel Safety',
          content: 'Verify bookings, keep emergency contacts, and follow local laws and customs.',
        },
        {
          subtitle: 'Matching Safety',
          content: 'Do not send money to other users and meet in public places only.',
        },
        {
          subtitle: 'AI & Travel Risk Disclaimer',
          content: 'AI-generated recommendations are indicative only and may contain inaccuracies. Users must independently verify all travel details. Travel involves inherent risks. Sync and Explore is not liable for losses, injuries, or disputes arising from travel or user interactions.',
        },
      ],
    },
    {
      id: 'cookies',
      type: 'expandable',
      title: 'Cookie Policy',
      shortLabel: 'Cookies',
      icon: <FileText size={18} />,
      subsections: [
        {
          subtitle: 'What Are Cookies?',
          content: 'Cookies are small text files stored on your device that help us recognize you and enhance your experience. They contain your preferences and usage information.',
        },
        {
          subtitle: 'Types of Cookies We Use',
          content: 'Essential cookies enable core functionality and security. Analytics cookies help us understand how users interact with our platform. Preference cookies remember your settings and choices. Marketing cookies may be used to show relevant content.',
        },
        {
          subtitle: 'Your Cookie Choices',
          content: 'You can control cookies through your browser settings. Disabling essential cookies may affect platform functionality. Third-party providers may also set cookies on your device. Manage these through your browser\'s privacy settings.',
        },
        {
          subtitle: 'Third-Party Cookies',
          content: 'Our payment processors, analytics partners, and booking providers may set their own cookies. We recommend reviewing their privacy policies. You acknowledge that third-party services may collect data as per their terms.',
        },
        {
          subtitle: 'Cookie Duration & Updates',
          content: 'Most cookies expire after your session or within specified timeframes. We may update this policy as needed. Continued use of the platform signifies acceptance of updated cookie practices.',
        },
      ],
    },
    {
      id: 'gdpr',
      type: 'expandable',
      title: 'GDPR & Data Rights',
      shortLabel: 'GDPR',
      icon: <Shield size={18} />,
      subsections: [
        {
          subtitle: 'Your Data Rights',
          content: 'You have the right to access, correct, delete, or port your personal data. You can request restrictions on processing or object to specific uses. To exercise these rights, contact us with your request.',
        },
        {
          subtitle: 'Data Processing Basis',
          content: 'We process your data based on legitimate interests in providing services, consent for marketing, contract fulfillment, and legal compliance. You can withdraw consent for non-essential processing at any time.',
        },
        {
          subtitle: 'Data Protection & International Transfers',
          content: 'We implement security measures including encryption and secure servers. Data may be transferred internationally to provide services. We ensure appropriate safeguards under GDPR standards. Your data is protected by our Data Protection Agreement.',
        },
        {
          subtitle: 'Data Retention & Deletion',
          content: 'We retain personal data for as long as necessary to provide services and fulfill legal obligations. Upon account deletion, data is securely erased within 30 days, except where required by law. You can request data deletion anytime.',
        },
        {
          subtitle: 'Privacy by Design',
          content: 'We implement privacy protections from the outset in all systems and processes. GDPR compliance is built into our data handling practices. Regular audits ensure we meet standards. We conduct Data Protection Impact Assessments for high-risk processing.',
        },
        {
          subtitle: 'Data Controller & DPO',
          content: 'Sync and Explore acts as the data controller for personal information. For data-related inquiries, contact privacy@syncandexplore.com. Our Data Protection Officer is available to address GDPR concerns and ensure compliance.',
        },
      ],
    },
  ];

  return (
    <div className="terms-premium-container">
      {/* Scroll Progress Bar */}
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }}></div>

      {/* Premium Banner */}
      <header ref={headerRef} className="terms-premium-banner">
        <div className="banner-backdrop-mesh"></div>
        <div className="banner-pattern-overlay"></div>
        
        <button onClick={() => navigate('/')} className="banner-back-btn" aria-label="Go back">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div className="banner-content">
          <div className="banner-badge-group">
             <div className="last-updated-pill">
              <Clock size={14} />
              <span>Last Updated: January 2026</span>
            </div>
          </div>

          <h1 className="banner-title">
            Terms & <span className="text-highlight">Conditions</span>
          </h1>
          
          <p className="banner-subtitle">
            Clear guidelines for a seamless journey. Built for trust, designed for travelers.
          </p>

          <div className="banner-trust-indicators">
            <div className="trust-item">
              <Shield size={18} />
              <span>Secure Platform</span>
            </div>
            <div className="trust-divider"></div>
            <div className="trust-item">
              <Eye size={18} />
              <span>Transparent</span>
            </div>
            <div className="trust-divider"></div>
            <div className="trust-item">
              <Heart size={18} />
              <span>User-First</span>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky Section Navigator */}
      <nav className="sticky-nav-premium">
        <div className="nav-tracks">
          <div className="nav-progress-fill" style={{ width: `${scrollProgress}%` }}></div>
        </div>

        <div className="nav-pills-container">
          {mainSections.map((section, index) => (
            <button
              key={section.id}
              className={`nav-pill-premium ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => {
                const element = sectionsRef.current[section.id];
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              title={section.title}
            >
              <span className="nav-pill-number">{String(index + 1).padStart(2, '0')}</span>
              <span className="nav-pill-label">{section.shortLabel}</span>
            </button>
          ))}
          {policyTitleSections.map((section, index) => (
            <button
              key={section.id}
              className={`nav-pill-premium ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => {
                const element = sectionsRef.current[section.id];
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              title={section.title}
            >
              <span className="nav-pill-number">{String(mainSections.length + index + 1).padStart(2, '0')}</span>
              <span className="nav-pill-label">{section.shortLabel}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main ref={contentRef} className="terms-premium-content">
        <div className="terms-content-wrapper">
          {/* Introduction Section */}
          <section
            className="premium-section intro-premium-section"
            ref={(el) => (sectionsRef.current['intro'] = el)}
          >
            <div className="section-intro-banner">
              <h2>Welcome to Our Terms</h2>
              <p>
                We believe legal documents should be clear, honest, and fair. This page explains the rules of using Sync and Explore in plain language. We've done our best to make these terms understandable—and we genuinely want your feedback if anything is confusing.
              </p>
            </div>
          </section>

          {/* Main Legal Sections */}
          {mainSections.map((section, index) => (
            <section
              key={section.id}
              ref={(el) => (sectionsRef.current[section.id] = el)}
              className={`premium-legal-card card-type-${section.type}`}
            >
              <div className="card-header-premium">
                <div className="card-number">{String(index + 1).padStart(2, '0')}</div>
                <div className="card-title-group">
                  <div className="card-icon">{section.icon}</div>
                  <h2>{section.title}</h2>
                </div>
              </div>

              <div className="card-summary-box">
                <p>{section.tldr}</p>
              </div>

              <button
                className={`expand-toggle ${expandedSections[section.id] ? 'expanded' : ''}`}
                onClick={() =>
                  setExpandedSections((p) => ({ ...p, [section.id]: !p[section.id] }))
                }
              >
                <span>{expandedSections[section.id] ? 'Hide Details' : section.ctaText}</span>
                <ChevronDown size={18} />
              </button>

              {expandedSections[section.id] && (
                <div className="card-content-expanded">
                  <p>{section.content}</p>
                  {section.bullets && (
                    <ul className="content-bullets">
                      {section.bullets.map((bullet, i) => (
                        <li key={i}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </section>
          ))}

          {/* AI Disclaimer Section */}
          <section className="ai-disclaimer-premium">
            <div className="disclaimer-icon-box">
              <AlertCircle size={32} />
            </div>
            <div className="disclaimer-content">
              <h3>AI Responsibility & Trust</h3>
              <p>
                Our AI provides recommendations, not guarantees. AI can sometimes make mistakes or give outdated info. <strong>Always verify details independently</strong> before booking or making decisions. You are responsible for checking prices, availability, legality, and safety. Sync and Explore is not liable for any losses from relying on AI suggestions.
              </p>
            </div>
          </section>

          {/* Policy Sections */}
          {policyTitleSections.map((policySection, policyIndex) => (
            <section
              key={policySection.id}
              ref={(el) => (sectionsRef.current[policySection.id] = el)}
              className={`premium-legal-card card-type-${policySection.type}`}
            >
              <div className="card-header-premium">
                <div className="card-number">{String(mainSections.length + policyIndex + 1).padStart(2, '0')}</div>
                <div className="card-title-group">
                  <div className="card-icon">{policySection.icon}</div>
                  <h2>{policySection.title}</h2>
                </div>
              </div>

              <button
                className={`expand-toggle ${expandedSections[policySection.id] ? 'expanded' : ''}`}
                onClick={() =>
                  setExpandedSections((p) => ({ ...p, [policySection.id]: !p[policySection.id] }))
                }
              >
                <span>{expandedSections[policySection.id] ? 'Hide Details' : policySection.ctaText}</span>
                <ChevronDown size={18} />
              </button>

              {(expandedSections[policySection.id] || policySection.type === 'highlight') && (
                <div className="policy-subsections">
                  {policySection.subsections.map((subsection, subIndex) => (
                    <div key={subIndex} className="subsection-premium">
                      <h3>{subsection.subtitle}</h3>
                      <p>{subsection.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          {/* Trust & Safety Grid */}
          <section className="trust-safety-premium">
            <h2>Your Safety Matters</h2>
            <p className="section-subtitle-premium">
              We've built features to help you stay safe on every journey.
            </p>

            <div className="trust-grid">
              <div className="trust-card">
                <div className="trust-card-icon travel-icon">
                  <MapPin size={28} />
                </div>
                <h3>Travel Safety</h3>
                <ul>
                  <li>Verify bookings independently</li>
                  <li>Keep emergency contact info</li>
                  <li>Follow local laws & customs</li>
                  <li>Get travel insurance</li>
                </ul>
              </div>

              <div className="trust-card">
                <div className="trust-card-icon matching-icon">
                  <Users size={28} />
                </div>
                <h3>Buddy Matching Safety</h3>
                <ul>
                  <li>Never send money to users</li>
                  <li>Meet in public places only</li>
                  <li>Trust your instincts</li>
                  <li>Report suspicious profiles</li>
                </ul>
              </div>

              <div className="trust-card">
                <div className="trust-card-icon security-icon">
                  <Lock size={28} />
                </div>
                <h3>Payment Security</h3>
                <ul>
                  <li>Secure payment processing</li>
                  <li>Never share payment details</li>
                  <li>Monitor your accounts</li>
                  <li>Fraud protection</li>
                </ul>
              </div>
            </div>
          </section>

          {/* User Acknowledgement Section */}
          <section className="acknowledgement-premium">
            <div className="ack-card-glass">
              <div className="ack-icon-top">
                <CheckCircle size={32} />
              </div>

              <h2>Ready to Explore?</h2>

              <p className="ack-description">
                By proceeding, you confirm that you've read and understood these Terms & Conditions, our Privacy Policy, and Community Guidelines. You accept our terms of service and agree to use Sync and Explore responsibly.
              </p>

              <label className="ack-checkbox-premium">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="ack-input"
                />
                <span className="checkbox-custom"></span>
                <span className="ack-label-text">I understand and accept these terms</span>
              </label>

              <button
                className={`ack-cta-premium ${accepted ? 'enabled' : 'disabled'}`}
                disabled={!accepted}
                onClick={() => {
                  if (accepted) navigate('/dashboard');
                }}
              >
                <span>Continue Exploring</span>
                <ChevronUp size={18} style={{ transform: 'rotate(-90deg)' }} />
              </button>

              <p className="ack-support-text">
                Questions? <a href="mailto:hello@syncandexplore.com">Contact our support team</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Floating Back to Top */}
      {showScrollTop && (
        <button
          className="floating-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <ChevronUp size={22} />
        </button>
      )}
    </div>
  );
};

export default TermsAndServices;
// commit 